/**
 * Finding text inside an ingested source file.
 *
 * The source viewer could open the original email, the sponsor's workbook and
 * the OM, and offered no way to look for anything in them — on a 90-page
 * offering memorandum or a 14-sheet model, "is the guarantor named anywhere"
 * meant reading. This is the matching half: pure, so the highlighting and the
 * match counter cannot disagree about what a match is.
 *
 * Deliberately a plain case-insensitive substring search, not a regex: an
 * operator typing `$1,250,000` or `(212)` must get that text, not a syntax
 * error, and every character a broker writes is a literal here.
 */

/** One hit: the half-open range `[start, end)` within its line. */
export type Match = { line: number; start: number; end: number }

/** A rendered run of a line — `hit` marks it as part of a match. */
export type Segment = { text: string; hit: boolean; index: number }

export function normalizeQuery(query: string): string {
  return query.trim().toLowerCase()
}

/**
 * Every occurrence of `query` in `lines`, in reading order.
 *
 * Overlapping is impossible by construction (the scan advances past each hit),
 * so the count a user sees is the count they can step through.
 */
export function findMatches(lines: string[], query: string): Match[] {
  const needle = normalizeQuery(query)
  if (!needle) return []
  const matches: Match[] = []
  lines.forEach((line, lineIndex) => {
    const haystack = line.toLowerCase()
    let from = 0
    for (;;) {
      const at = haystack.indexOf(needle, from)
      if (at < 0) break
      matches.push({ line: lineIndex, start: at, end: at + needle.length })
      from = at + needle.length
    }
  })
  return matches
}

/**
 * One line split into plain and matching runs, each carrying its index in the
 * document-wide match list so the active hit can be styled and scrolled to.
 *
 * `firstMatchIndex` is the running total of matches on the lines before this
 * one — the caller holds it, because only the caller knows the whole document.
 */
export function segmentLine(line: string, query: string, firstMatchIndex: number): Segment[] {
  const needle = normalizeQuery(query)
  if (!needle) return [{ text: line, hit: false, index: -1 }]

  const haystack = line.toLowerCase()
  const segments: Segment[] = []
  let cursor = 0
  let index = firstMatchIndex
  for (;;) {
    const at = haystack.indexOf(needle, cursor)
    if (at < 0) break
    if (at > cursor) segments.push({ text: line.slice(cursor, at), hit: false, index: -1 })
    segments.push({ text: line.slice(at, at + needle.length), hit: true, index })
    index += 1
    cursor = at + needle.length
  }
  if (cursor < line.length) segments.push({ text: line.slice(cursor), hit: false, index: -1 })
  return segments.length ? segments : [{ text: line, hit: false, index: -1 }]
}

/** Does this cell value, as the grid renders it, contain the query? */
export function cellMatches(value: unknown, query: string): boolean {
  const needle = normalizeQuery(query)
  if (!needle) return false
  if (value === null || value === undefined) return false
  const rendered = typeof value === 'number' ? value.toLocaleString() : String(value)
  // A number is searchable both as typed and as displayed: an operator hunting
  // 2904000 and one hunting 2,904,000 are looking for the same cell.
  return (
    rendered.toLowerCase().includes(needle) ||
    String(value).toLowerCase().includes(needle)
  )
}

/** Step through matches, wrapping at both ends. */
export function stepMatch(current: number, total: number, delta: number): number {
  if (total <= 0) return 0
  return (((current + delta) % total) + total) % total
}
